/**
 * Diagnostic message catalog + renderer. The engine stays locale-agnostic: it
 * emits `{code, msgKey, params}` (plus a baked English `message`), and each
 * consumer renders in ITS OWN locale via `diagnosticText()` — the viewer in the
 * UI locale, the LSP in the editor's `InitializeParams.locale`, the CLI in
 * English. Quick-fixes read `params`, never the rendered prose.
 */

import IntlMessageFormat from "intl-messageformat";
import type { Diagnostic, DiagnosticSeverity, SourceSpan } from "../types.js";
import type { DiagnosticCode } from "./codes.js";
import { DIAG_EN } from "./catalog.en.js";
import { DIAG_RU } from "./catalog.ru.js";
import { DIAG_PARSE_EN } from "./catalog.parse.en.js";
import { DIAG_PARSE_RU } from "./catalog.parse.ru.js";

export type DiagLocale = "en" | "ru";

const CATALOGS: Record<DiagLocale, Record<string, string>> = {
  en: { ...DIAG_EN, ...DIAG_PARSE_EN },
  ru: { ...DIAG_RU, ...DIAG_PARSE_RU },
};

function normLocale(locale: string | undefined): DiagLocale {
  return locale && locale.slice(0, 2).toLowerCase() === "ru" ? "ru" : "en";
}

/**
 * A catalog template as ICU sees it.
 *
 * Two normalizations, both so authors can write natural prose:
 *
 * - Diagnostics quote identifiers with straight apostrophes (`'{name}'`), but
 *   ICU treats `'` as its escape char — double every one to make it literal.
 * - A template that needs a LITERAL brace (to show `release v3 { at: "…" }`)
 *   writes `\{` / `\}`. Doubling the brace instead would be ambiguous: ICU's
 *   own `select`/`plural` forms legitimately end in `}}` (`other{}}`), so a
 *   blanket doubled-brace rule would corrupt them. A backslash appears nowhere
 *   in ICU syntax, so it can only mean "the author wanted a brace here".
 *
 * Order matters: apostrophes are doubled FIRST, so the quoting apostrophes this
 * inserts around braces stay single and keep their ICU meaning.
 */
function toIcu(tpl: string): string {
  return tpl.replace(/'/g, "''").replace(/\\\{/g, "'{'").replace(/\\\}/g, "'}'");
}

/**
 * Render a diagnostic message from its catalog template in `locale`. Falls back
 * to the English template, then `undefined` when the key is unknown (so callers
 * can fall back to the baked `message`).
 */
export function renderDiagnostic(
  msgKey: string,
  params: Record<string, unknown> | undefined,
  locale: string | undefined,
): string | undefined {
  const loc = normLocale(locale);
  const tpl = CATALOGS[loc][msgKey] ?? CATALOGS.en[msgKey];
  if (tpl === undefined) return undefined;
  const body = renderTemplate(tpl, params, loc);
  return body + didYouMeanTail(params, loc);
}

/**
 * The did-you-mean tail for `params.didYouMean` — the ONE place a suggestion
 * becomes prose. Emit sites put candidates in `params` as a string array and
 * never touch the message, so a quick-fix reads the array instead of parsing
 * quotes back out of English. Rendered in the reader's locale, with
 * `Intl.ListFormat` picking the disjunction wording.
 */
function didYouMeanTail(params: Record<string, unknown> | undefined, loc: DiagLocale): string {
  const cands = params?.["didYouMean"];
  if (!Array.isArray(cands) || cands.length === 0) return "";
  const list = new Intl.ListFormat(loc, { type: "disjunction" }).format(cands.map((c) => `'${String(c)}'`));
  return renderTemplate(CATALOGS[loc]["common.didYouMean"] ?? CATALOGS.en["common.didYouMean"]!, { list }, loc);
}

function renderTemplate(
  tpl: string,
  params: Record<string, unknown> | undefined,
  loc: DiagLocale,
): string {
  try {
    // ignoreTag: diagnostic prose uses literal angle brackets (`<Parent>`,
    // `<type>`), never ICU rich-text tags — don't let IntlMessageFormat parse
    // them as markup.
    return String(new IntlMessageFormat(toIcu(tpl), loc, undefined, { ignoreTag: true }).format(params ?? {}));
  } catch {
    // A missing interpolation var makes IntlMessageFormat throw — and returning
    // the raw template would leak EVERY placeholder to the user (a whole class
    // of bug an emit site can introduce). Retry with all simple placeholders
    // defaulted to "", so only a genuinely-forgotten value blanks out instead of
    // the message dissolving into `{close} {what} {got}`.
    try {
      const names = new Set([...tpl.matchAll(/(?<!\\)\{\s*([a-zA-Z0-9_]+)\s*[,}]/g)].map((m) => m[1]));
      const filled: Record<string, unknown> = {};
      for (const n of names) if (n) filled[n] = "";
      return String(
        new IntlMessageFormat(toIcu(tpl), loc, undefined, { ignoreTag: true }).format({
          ...filled,
          ...params,
        }),
      );
    } catch {
      return tpl;
    }
  }
}

/**
 * The one call every consumer uses: the catalogued render of `d` in `locale`,
 * or the baked English `message` when `d` carries no key (or it is
 * uncatalogued). Keeps un-migrated diagnostics working unchanged.
 */
export function diagnosticText(
  d: { readonly message: string; readonly msgKey?: string; readonly params?: Readonly<Record<string, unknown>> },
  locale: string | undefined,
): string {
  if (d.msgKey) {
    const r = renderDiagnostic(d.msgKey, d.params as Record<string, unknown> | undefined, locale);
    if (r !== undefined) return r;
  }
  return d.message;
}

/**
 * Build a diagnostic from a catalog key. The English `message` is derived from
 * the catalog (so it never drifts from the key), while `msgKey`+`params` ride
 * along for per-locale rendering and quick-fixes. The migration target for
 * every construction site.
 */
export function makeDiag(
  severity: DiagnosticSeverity,
  source: SourceSpan | undefined,
  code: DiagnosticCode,
  msgKey: string,
  params?: Readonly<Record<string, unknown>>,
): Diagnostic {
  const message = renderDiagnostic(msgKey, params as Record<string, unknown> | undefined, "en") ?? msgKey;
  return { severity, message, source, code, msgKey, params };
}

export { DIAG_EN, DIAG_RU };
